import React, { useState, useEffect } from "react";
import "./App.css";
import emailjs from '@emailjs/browser';
import config from "./config";
import Step3 from "./step3"; // The new step3 page


export default function App() {
  const [step, setStep] = useState(1);
  const [propertyType, setPropertyType] = useState("");
  const [occupancy, setOccupancy] = useState("");
  const [energyUsage, setEnergyUsage] = useState("");
  const [actualUsage, setActualUsage] = useState("");
  const [panels, setPanels] = useState(20);
  const [battery, setBattery] = useState("");
  const [evCharger, setEvCharger] = useState("");
  const [importPrice, setImportPrice] = useState(0.2635);
  const [exportPrice, setExportPrice] = useState(0.15);
  const [email, setEmail] = useState("");
  const [estimate, setEstimate] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideSticky1, setHideSticky1] = useState(false);
  const [hideSticky2, setHideSticky2] = useState(false);
  const [message, setMessage] = useState("");  // for success/error feedback
const [showStep3, setShowStep3] = useState(false);

function getPvPricePerW(systemSize) {
  const band = config.pvBands.find(
    (b) => systemSize >= b.min && systemSize < b.max
  );
  return band ? band.pricePerW : 1.3; // fallback
}

// Fetch settings from WordPress (uses localized EE_SETTINGS)
useEffect(() => {
  const initDefaults = () => {
    const defaults = window.EE_SETTINGS && window.EE_SETTINGS.defaults ? window.EE_SETTINGS.defaults : null;
    if (defaults) {
      // Ensure numeric types
      const typed = {
        import_price: Number(defaults.import_price),
        export_price: Number(defaults.export_price),
        panel_wattage: Number(defaults.panel_wattage),
        domestic_apr: Number(defaults.domestic_apr),
        domestic_term: Number(defaults.domestic_term),
        domestic_usage: defaults.domestic_usage || { low: 1800, medium: 2700, high: 3600 },
        commercial_apr: Number(defaults.commercial_apr),
        commercial_term: Number(defaults.commercial_term),
        commercial_usage: defaults.commercial_usage || { low: 10000, medium: 20000, high: 40000 },
        batteries: defaults.batteries || {
          "5kWh-non": 2500,
          "10kWh-non": 4500,
          "5kWh-int": 3500,
          "10kWh-int": 5500,
          "9.5kWh-pre": 8000
        },
        ev_chargers: defaults.ev_chargers || { smart: 799, premium: 899 },
        yield_factor: Number(defaults.yield_factor),
        loss_factor: Number(defaults.loss_factor),
        co2_factor: Number(defaults.co2_factor),
      };
      setSettings(typed);
      // set import/export states too so visible UI controls reflect WP values
      if (typeof typed.import_price !== 'undefined') setImportPrice(typed.import_price);
      if (typeof typed.export_price !== 'undefined') setExportPrice(typed.export_price);
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const restUrl = (window.EE_SETTINGS && window.EE_SETTINGS.rest_url) ? window.EE_SETTINGS.rest_url : `${window.location.origin}/wp-json/solar-calculator/v1/settings`;
      const response = await fetch(restUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Failed to fetch settings from REST');
      const data = await response.json();

      // Normalize numeric types
      const typed = {
        import_price: Number(data.import_price),
        export_price: Number(data.export_price),
        panel_wattage: Number(data.panel_wattage),
        domestic_apr: Number(data.domestic_apr),
        domestic_term: Number(data.domestic_term),
        domestic_usage: data.domestic_usage,
        commercial_apr: Number(data.commercial_apr),
        commercial_term: Number(data.commercial_term),
        commercial_usage: data.commercial_usage,
        batteries: data.batteries,
        ev_chargers: data.ev_chargers,
        yield_factor: Number(data.yield_factor),
        loss_factor: Number(data.loss_factor),
        co2_factor: Number(data.co2_factor),
      };

      setSettings(typed);

      if (typeof typed.import_price !== 'undefined') setImportPrice(typed.import_price);
      if (typeof typed.export_price !== 'undefined') setExportPrice(typed.export_price);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // If fetch fails and no defaults were set, set a safe fallback (only used as last resort)
      if (!settings) {
        setSettings({
          import_price: 0.2635,
          export_price: 0.15,
          panel_wattage: 475,
          domestic_apr: 0.129,
          domestic_term: 10,
          domestic_usage: { low: 1800, medium: 2700, high: 3600 },
          commercial_apr: 0.089,
          commercial_term: 7,
          commercial_usage: { low: 10000, medium: 20000, high: 40000 },
          batteries: {
            "5kWh-non": 2500,
            "10kWh-non": 4500,
            "5kWh-int": 3500,
            "10kWh-int": 5500,
            "9.5kWh-pre": 8000,
          },
          ev_chargers: { smart: 799, premium: 899 },
          yield_factor: 900,
          loss_factor: 0.9,
          co2_factor: 0.2,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Use localized defaults first for instant UI consistency, then fetch to refresh
  initDefaults();
  fetchSettings();
}, []); // run once



  const batteryOptions = [
    { value: "5kWh-non", label: "5 kWh", sublabel: "(Non‑Intelligent Battery)", tooltip: "Pylontech US5000 — compact LiFePO₄ battery for smaller solar setups. Safe, durable, and cost-effective." },
    { value: "10kWh-non", label: "10 kWh", sublabel: "(Non‑Intelligent Battery)", tooltip: "2 × Pylontech US5000 — dual-battery setup providing double capacity. Proven, modular, and maintenance-free." },
    { value: "5kWh-int", label: "5 kWh", sublabel: "(Intelligent Battery)", tooltip: "Fox ESS EP5.18-H — smart lithium battery with app monitoring and modular expandability. Ideal for modern solar homes." },
    { value: "10kWh-int", label: "10 kWh", sublabel: "(Intelligent Battery)", tooltip: "Fox ESS EP11-H — high-capacity smart battery for large homes or EV users. Fast charging, efficient, and future-ready." },
    { value: "9.5kWh-pre", label: "9.5 kWh", sublabel: "(Premium Smart Battery)", tooltip: "GivEnergy 9.5 kWh Li-ion — premium UK-made smart battery with cloud monitoring, tariff optimisation, and long warranty." },
  ];

  const evOptions = [
    { 
      value: "basic", 
      label: "No Thanks", 
      tooltip: "Not Interested in EV Charging" 
    },
    { 
      value: "smart", 
      label: "Ohme Home Pro", 
      tooltip: "Latest-generation smart EV charger with LCD screen, app control, and tariff optimisation for cost-efficient home charging. Supplied with a 5 m tethered cable." 
    },
    { 
      value: "premium", 
      label: "Hypervolt Home Pro", 
      tooltip: "Premium smart charger with Wi-Fi connectivity, solar integration, and automatic load balancing for seamless home use. Includes a 5 m tethered cable (optional 7.5 m or 10 m upgrades available)." 
    },
  ];

  // Usage lookup based on property type - USING WORDPRESS SETTINGS
  const usageLookup = settings ? (
    propertyType === "commercial" 
      ? settings.commercial_usage 
      : settings.domestic_usage
  ) : { low: 1800, medium: 2700, high: 3600 };
  
  const approximateUsage = energyUsage ? usageLookup[energyUsage] : null;

  const minPanels = propertyType === "commercial" ? 20 : 6;
  const maxPanels = propertyType === "commercial" ? 150 : 30;


  //email woorkflow using emailjs
  const sendEmail = (data) => {
    if (!email) {
      setMessage("Please enter your email!");
      return;
    }

    setLoading(true);
    setMessage("");

    emailjs
      .send(
        "service_7m5yclj",
        "template_4pbff0y",
        {
          to_email: email,
          panels: data.panels,
          battery: data.battery,
          evCharger: data.evCharger,
          estimate: result.totalInstalledCost?.toFixed(0) || 0,
        },
        "gEBe2ZowUwsb4bmMq"
      )
      .then(
        (result) => {
          setLoading(false);
          setMessage("Email sent successfully!");
        },
        (error) => {
          setLoading(false);
          setMessage("Error sending email. Please try again.");
          console.error(error);
        }
      );
  };


  const calculate = () => {
    if (loading || !settings) {
      return {}; // Return empty while loading
    }

    let annualUsage;
    let selfConsumption;
    let apr;
    let termYears;
    let deposit = 0;

    let annualGen, systemSize, r;

    // Use ALL WordPress settings instead of hardcoded values
    const panelWatt = settings.panel_wattage;
    const yieldFactor = settings.yield_factor;
    const lossFactor = settings.loss_factor;
    const co2Factor = settings.co2_factor;

    if (propertyType === "commercial") {
      // Commercial Values - USING WORDPRESS SETTINGS
      const usageTiers = settings.commercial_usage;
      annualUsage = actualUsage ? parseInt(actualUsage, 10) : (usageTiers[energyUsage] || 20000);
      selfConsumption = 55; // fixed percentage (55%)
      apr = settings.commercial_apr;
      termYears = settings.commercial_term;
      deposit = 0;
      
      systemSize = (panels * panelWatt) / 1000; // kW
      annualGen = systemSize * yieldFactor * lossFactor; // kWh

      const selfConsumed = (annualUsage * selfConsumption) / 100;
      const exportKwh = Math.max(0, annualGen - selfConsumed);

      // ---- Financials ----
      const billSavings = selfConsumed * importPrice;
      const exportIncome = exportKwh * exportPrice;
      const totalValue = billSavings + exportIncome;

      // ---- Costs ----
      const pricePerW = getPvPricePerW(systemSize);
      const pvCost = systemSize * 1000 * pricePerW;
      const batteryCost = 0;
      const evCost = 0;
      const totalInstalledCost = pvCost + batteryCost + evCost;

      // ---- Finance ----
      const loanPrincipal = totalInstalledCost - deposit;
      const monthlyRate = apr / 12;
      const n = termYears * 12;
      const monthlyPayment =
        (loanPrincipal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n));
      const monthlySavings = totalValue / 12;
      const netMonthly = monthlySavings - monthlyPayment;
      const simplePayback = totalInstalledCost / totalValue;

      // ---- CO₂ ----
      const co2Avoided = (annualGen * co2Factor) / 1000;
      
      return {
        annualUsage,
        systemSize,
        annualGen,
        selfConsumption,
        selfConsumed,
        exportKwh,
        billSavings,
        exportIncome,
        totalValue,
        pvCost,
        batteryCost,
        evCost,
        totalInstalledCost,
        monthlyPayment,
        monthlySavings,
        netMonthly,
        simplePayback,
        co2Avoided,
      };
    } else {
      // Domestic Values - USING WORDPRESS SETTINGS
      const usageTiers = settings.domestic_usage;
      annualUsage = actualUsage ? parseInt(actualUsage, 10) : (usageTiers[energyUsage] || 2700);
      apr = settings.domestic_apr;
      termYears = settings.domestic_term;

      systemSize = (panels * panelWatt) / 1000;
      annualGen = systemSize * yieldFactor * lossFactor;
      r = annualGen / annualUsage;
    }

    // Self-consumption calculations (same as before)
    let pvSelf = 0;

    if (occupancy === "home") {
      pvSelf = 92 - (r - 0.25) * (92 - 82) / (0.5 - 0.25);
      if (r > 0.5) pvSelf = 82 - (r - 0.5) * (82 - 70) / (1 - 0.5);
      if (r > 1) pvSelf = 70 - (r - 1) * (70 - 60) / (1.5 - 1);
      if (r > 1.5) pvSelf = 60 - (r - 1.5) * (60 - 45) / (2.5 - 1.5);
    }
    
    if (occupancy === "half") {
      pvSelf = 85 - (r - 0.25) * (85 - 72) / (0.5 - 0.25);
      if (r > 0.5) pvSelf = 72 - (r - 0.5) * (72 - 55) / (1 - 0.5);
      if (r > 1) pvSelf = 55 - (r - 1) * (55 - 45) / (1.5 - 1);
      if (r > 1.5) pvSelf = 45 - (r - 1.5) * (45 - 32) / (2.5 - 1.5);
    }
    
    if (occupancy === "out") {
      pvSelf = 78 - (r - 0.25) * (78 - 60) / (0.5 - 0.25);
      if (r > 0.5) pvSelf = 60 - (r - 0.5) * (60 - 42) / (1 - 0.5);
      if (r > 1) pvSelf = 42 - (r - 1) * (42 - 33) / (1.5 - 1);
      if (r > 1.5) pvSelf = 33 - (r - 1.5) * (33 - 24) / (2.5 - 1.5);
    }

    // Battery uplift
    const batteryData = {
      "5kWh-non": { capture: 0.60, efficiency: 0.90 },
      "10kWh-non": { capture: 0.70, efficiency: 0.90 },
      "5kWh-int": { capture: 0.85, efficiency: 0.93 },
      "10kWh-int": { capture: 0.90, efficiency: 0.93 },
      "9.5kWh-pre": { capture: 0.95, efficiency: 0.95 },
      none: { capture: 0, efficiency: 1 },
    };

    const selectedBattery = batteryData[battery] || { capture: 0, efficiency: 1 };

    let batteryUplift = 0;
    if (propertyType !== "commercial") {
      const pvSelfFraction = pvSelf / 100;
      const theoreticalUplift = selectedBattery.capture * selectedBattery.efficiency * 0.9;
      const maxUpliftFraction = 0.95 - pvSelfFraction;
      const cappedUpliftFraction = Math.max(0, Math.min(maxUpliftFraction, theoreticalUplift));
      batteryUplift = cappedUpliftFraction * 100;
    }

    selfConsumption = Math.min(100, pvSelf + batteryUplift);
    
    // Self-consumed & export
    let selfConsumed = (annualUsage * selfConsumption) / 100;
    const exportKwh = Math.max(0, annualGen - selfConsumed);

    // Financials
    const billSavings = selfConsumed * importPrice;
    const exportIncome = exportKwh * exportPrice;
    const totalValue = billSavings + exportIncome;

    // Costs - USING WORDPRESS SETTINGS
    const pricePerW = getPvPricePerW(systemSize);
    const pvCost = systemSize * 1000 * pricePerW;

    // Battery costs from WordPress settings
    const batteryCosts = {
      none: 0,
      "5kWh-non": settings.batteries["5kWh-non"],
      "10kWh-non": settings.batteries["10kWh-non"],
      "5kWh-int": settings.batteries["5kWh-int"],
      "10kWh-int": settings.batteries["10kWh-int"],
      "9.5kWh-pre": settings.batteries["9.5kWh-pre"],
    };
    const batteryCost = batteryCosts[battery] || 0;

    // EV costs from WordPress settings
    const evCost = propertyType === "domestic" && evCharger === "smart" ? settings.ev_chargers.smart :
                   propertyType === "domestic" && evCharger === "premium" ? settings.ev_chargers.premium : 0;
    
    const totalInstalledCost = pvCost + batteryCost + evCost;

    // Finance
    const loanPrincipal = totalInstalledCost - deposit;
    const monthlyRate = apr / 12;
    const n = termYears * 12;
    const monthlyPayment =
      (loanPrincipal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n));
    const monthlySavings = totalValue / 12;
    const netMonthly = monthlySavings - monthlyPayment;
    const simplePayback = totalInstalledCost / totalValue;

    // CO₂ - USING WORDPRESS SETTINGS
    const co2Avoided = (annualGen * co2Factor) / 1000;

    return {
      annualUsage,
      systemSize,
      annualGen,
      selfConsumption,
      selfConsumed,
      exportKwh,
      billSavings,
      exportIncome,
      totalValue,
      pvCost,
      batteryCost,
      evCost,
      totalInstalledCost,
      monthlyPayment,
      monthlySavings,
      netMonthly,
      simplePayback,
      co2Avoided,
    };
  };

  const result = calculate();
  
  // Rest of your existing functions remain the same
  const handleNext = (stepNumber) => {
    setStep(stepNumber);
  };

  const handleBlockClick = () => {
    setShowInput(!showInput);
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (propertyType === "domestic") {
      setPanels(10);
    } else if (propertyType === "commercial") {
      setPanels(20);
    }
  }, [propertyType]);

  // THE REST OF YOUR RETURN JSX REMAINS EXACTLY THE SAME
  // Only the calculate function and settings fetch are modified
  useEffect(() => {
  const handleScroll = () => {
    const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);

    // Div 1 hides at 75%
    setHideSticky1(scrollPercent > 0.52);

    // Div 2 hides at 50%
    setHideSticky2(scrollPercent < 0.23);
  };

  window.addEventListener("scroll", handleScroll);
  return () => window.removeEventListener("scroll", handleScroll);
}, []);


if (showStep3) {
  return (
    <Step3
      panels={panels}                          // state value
      battery={battery}                        // state value
      evCharger={evCharger}                    // state value
      estimate={result.totalInstalledCost?.toFixed(0) || 0}  // calculated value
      userEmail={email}   
                           // email from state
    />
  );
}

  return (
    <div className="container">
      <div className="step-navigation">
        <div className="step-info">Step {step} of 3</div>
        <div className="dots">
          <span
            className={step === 1 ? "active" : step > 1 ? "completed" : ""}
            onClick={() => {
              setStep(1);
              setShowStep3(false);
            }}
          ></span>
          <span
            className={step === 2 ? "active" : step > 2 ? "completed" : ""}
            onClick={() => {
              setStep(2);
              setShowStep3(false);
            }}
          ></span>
          <span
            className={step === 3 ? "active" : step > 3 ? "completed" : ""}
            onClick={() => {
              setStep(3);
              setShowStep3(true);
            }}
          ></span>
        </div>
      </div>

      {step === 1 ? (
        <>
          <h1>
            Find Out How Much You Can <span className="highlight">Save</span>
          </h1>
          <p className="subtitle">
            Answer a few quick questions to get your personalised energy savings estimate. It only takes a minute to see your potential impact.
          </p>

          {/* Property Type and Occupancy Selection */}
          <div className="card">
            <h2>Property Type?</h2>
            <p className="card-subtitle">
              Select the type of property you want to calculate savings for:
            </p>
            <div className="btn-row">
              <button
                className={`option-btn ${propertyType === "domestic" ? "active" : ""}`}
                onClick={() => setPropertyType("domestic")}
              >
                🏠 Domestic
                <span className="option-desc">Homes, apartments, residential buildings</span>
                {propertyType === "domestic" && (
                  <div
                    className="icon"
                    style={{
                      backgroundImage: `url("https://electroenergy.co.uk/wp-content/uploads/2025/11/Group-1.png")`,
                      backgroundSize: 'contain', 
                      backgroundRepeat: 'no-repeat',
                      width: '20px', 
                      height: '20px', 
                    }}
                    alt="checked"
                  ></div>
                )}
              </button>

              <button
                className={`option-btn ${propertyType === "commercial" ? "active" : ""}`}
                onClick={() => setPropertyType("commercial")}
              >
                🏢 Commercial / Industrial
                <span className="option-desc">Offices, retail, warehouses, factories</span>
                {propertyType === "commercial" && (
                  <div
                    className="icon"
                    style={{
                      backgroundImage: `url("https://electroenergy.co.uk/wp-content/uploads/2025/11/Group-1.png")`,
                      backgroundSize: 'contain', 
                      backgroundRepeat: 'no-repeat',
                      width: '20px', 
                      height: '20px', 
                    }}
                    alt="checked"
                  ></div>
                )}
              </button>
            </div>

            {/* Occupancy Question (Only for Domestic) */}
            {propertyType === "domestic" && (
              <div className="occupancy-selection">
                <h3>How much time is the home occupied during the day?</h3>
                <div className="btn-row">
                  {[
                    { key: "home", label: "Home All Day" },
                    { key: "half", label: "In Half the Day" },
                    { key: "out", label: "Out During the Day" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      className={`option-btn ${occupancy === key ? "active" : ""}`}
                      onClick={() => setOccupancy(key)}
                    >
                      {label}
                      {occupancy === key && (
                        <div
                          className="icon"
                          style={{
                            backgroundImage: `url("https://electroenergy.co.uk/wp-content/uploads/2025/11/Group-1.png")`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            width: '20px',
                            height: '20px',
                          }}
                          alt="checked"
                        ></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Show rest only after selection */}
          {propertyType && (
            <>
              {/* Usage Level Selection */}
              <div className="card">
                <h2>{propertyType === "commercial" ? "Select Your Usage Level" : "How Much Energy Do You Use?"}</h2>
                {propertyType !== "commercial" && (
                  <p className="card-subtitle">
                    Choose the option that best matches your typical energy consumption:
                  </p>
                )}
                <div className="usage-grid">
                  <div
                    className={`usage-box ${energyUsage === "low" ? "active" : ""}`}
                    onClick={() => {
                      setActualUsage("");
                      setEnergyUsage("low");
                    }}
                  >
                    <h3>Low</h3>
                    <p>
                      {propertyType === "commercial"
                        ? "Small offices or retail spaces with low energy demand."
                        : "Smaller homes (1–2 bedrooms), regularly empty on weekdays, newer builds (1970s+)."}
                    </p>
                    {energyUsage === "low" && (
                      <div
                        className="icon"
                        style={{
                          backgroundImage: `url("https://electroenergy.co.uk/wp-content/uploads/2025/11/Group-1.png")`,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          width: '20px',
                          height: '20px',
                        }}
                      ></div>
                    )}
                  </div>

                  <div
                    className={`usage-box ${energyUsage === "medium" ? "active" : ""}`}
                    onClick={() => {
                      setActualUsage("");
                      setEnergyUsage("medium");
                    }}
                  >
                    <h3>Medium</h3>
                    <p>
                      {propertyType === "commercial"
                        ? "Businesses with more employees and equipment, such as a small restaurant."
                        : "Around 3 bedrooms, 3–5 occupants, often occupied during the day."}
                    </p>
                    {energyUsage === "medium" && (
                      <div
                        className="icon"
                        style={{
                          backgroundImage: `url("https://electroenergy.co.uk/wp-content/uploads/2025/11/Group-1.png")`,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          width: '20px',
                          height: '20px',
                        }}
                      ></div>
                    )}
                  </div>

                  <div
                    className={`usage-box ${energyUsage === "high" ? "active" : ""}`}
                    onClick={() => {
                      setActualUsage("");
                      setEnergyUsage("high");
                    }}
                  >
                    <h3>High</h3>
                    <p>
                      {propertyType === "commercial"
                        ? "Greater demands, e.g. small manufacturing company or a larger retail chain"
                        : "Larger homes (4+ bedrooms), often older (pre‑1970s), higher heating demand."}
                    </p>
                    {energyUsage === "high" && (
                      <div
                        className="icon"
                        style={{
                          backgroundImage: `url("https://electroenergy.co.uk/wp-content/uploads/2025/11/Group-1.png")`,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          width: '20px',
                          height: '20px',
                        }}
                      ></div>
                    )}
                  </div>
                </div>
                <p className="approx-text">
                  Approximate annual usage:{" "}
                  <span className="approx-output">
                    {approximateUsage ? ` ${approximateUsage.toLocaleString()} kWh` : " — — —"}
                  </span>
                </p>

                <div>
                  {/* Actual Input Block */}
                  <div className="actual-block" onClick={handleBlockClick}>
                    <label>Actual</label>
                    <p>Know your exact usage? Enter your annual energy consumption:</p>
                    
                    {showInput && (
                      <div className="input-wrapper">
                        <input
                          type="number"
                          value={actualUsage}
                          onClick={handleInputClick}
                          onChange={(e) => {
                            const value = e.target.value;
                            setActualUsage(value);
                            if (value) {
                              setEnergyUsage("");
                            }
                          }}
                          placeholder="Enter kWh"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="price-block">
                  <label>Price per kWh</label>
                  <p>Enter the electricity price you currently pay per kilowatt‑hour (kWh).</p>
                  <div className="input-wrapper">
                    <span>£</span>
                    <input
                      type="number"
                      value={importPrice}
                      onChange={(e) => setImportPrice(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <small>It's fine if you're unsure — you can adjust anytime</small>
                </div>
              </div>

              <button
                className="next-btn"
                onClick={() => {
                  setStep(2);
                  window.scrollTo({ top: 0, behavior: "auto" });
                }}
                style={{
                  '--arrow-icon': `url("https://electroenergy.co.uk/wp-content/uploads/2025/11/Frame-5-1.png")`
                }}
              >
                LET'S GO
              </button>
            </>
          )}
        </>
      ) : (
        <div className="results-section">
          <h1>
            Customize Your Solar <span className="highlight">Setup</span>
          </h1>
          {propertyType === "commercial" && (
            <p className="commercial-tagline">
              Should the calculator not cover your requirements, simply choose Tailor My Quote to have a project manager provide a customised quote
            </p>
          )}
          {propertyType === "domestic" ? (
            
            <div className="results-grid">
             <div className={`mobile-sticky-estimate ${hideSticky1 ? "hidden" : ""}`}>
  <div className="estimate-label">Your Estimate</div>
  <div className="estimate-value">£{result.totalInstalledCost?.toFixed(0) || 0}</div>
</div>

              {/* Left column with panels, battery and EV options */}
              <div className="left-column">
                {/* Panel selection card */}
                <div className="panel-card">
                  <h3>Select Number of Panels?
                    <span className="info-icon">
                      i
                      <span className="tooltip-text">
                        Aiko Neostar 3S Gen 3 — high-efficiency all-black N-Type solar panel with advanced ABC cell technology, sleek design, and 25-year warranty.
                      </span>
                    </span>
                  </h3>
                  <p className="card-helper">Use the buttons to choose:</p>
                  <div className="panel-selector">
                    <button
                      className="panel-btn"
                      onClick={() => setPanels((p) => Math.max(minPanels, p - 1))}
                    >
                      −
                    </button>
                    <span className="panel-count">{panels}</span>
                    <button
                      className="panel-btn"
                      onClick={() => setPanels((p) => Math.min(maxPanels, p + 1))}
                    >
                      +
                    </button>
                  </div>
                  <p className="range-text">(Range: {minPanels}–{maxPanels} panels)</p>
                  <div className="note-bar">
                    On average, 10 panels suit a typical 3–bed home.
                  </div>
                </div>

                <div className="battery-card">
                  <div className="battery-options">
                    <h3>Select Battery Size?</h3>
                    <p className="card-helper">Not sure? Tap the "i" icons for help on differences.</p>

                    {batteryOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`battery-option ${battery === opt.value ? "active" : ""}`}
                        onClick={() => setBattery(opt.value)}
                      >
                        <span className={`radio-btn ${battery === opt.value ? "selected" : ""}`} />
                        <div className="battery-labels">
                          <span className="label">{opt.label}</span>
                          <span className="sublabel">{opt.sublabel}</span>
                        </div>
                        <span className="info-icon">
                          i
                          <span className="tooltip-text">{opt.tooltip}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="ev-card">
                  <h3>Add EV Charger (Home only)</h3>
                  <p className="card-helper">Not sure? Tap the "i" icons for help on differences.</p>
                  <div className="ev-options">
                    {evOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`ev-option ${evCharger === opt.value ? "active" : ""}`}
                        onClick={() => setEvCharger(opt.value)}
                      >
                        <span className={`radio-btn ${evCharger === opt.value ? "selected" : ""}`} />
                        <div className="ev-labels">
                          <span className="label">{opt.label}</span>
                          {opt.sublabel && <span className="sublabel">{opt.sublabel}</span>}
                        </div>
                        <span className="info-icon">
                          i
                          <span className="tooltip-text">{opt.tooltip}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right column with estimate and information */}
              
              <div className="right-column">
                
                <div className="estimate-card" style={{ backgroundImage: `url('https://electroenergy.co.uk/wp-content/uploads/2025/11/estimate-bg.png')` }}>
                  <div className="estimate-header">Your Estimate</div>
                  <div className="estimate-value">£{result.totalInstalledCost?.toFixed(0) || 0}</div>
                  <div className="savings-bar-container">
                    <div className="savings-bar">
                      <div className="savings-fill" style={{ width: "80%" }}></div>
                    </div>
                    <div className="savings-range">
                      Typical bill savings of{" "}
                      <span className="savings-value">
                        {(() => {
                          const savingPercent = (result.totalValue / (result.annualUsage * importPrice)) * 100 || 0;
                          const low = Math.floor(savingPercent * 0.9);
                          const high = Math.ceil(savingPercent * 1.1);
                          return `${low}–${high}%`;
                        })()}
                      </span>
                    </div>
                  </div>

                  <div className="stats-row wide">
                    <div>
                      <strong className="monthly-savings-label">Monthly Savings</strong>
                      <span className="monthly-savings-value">
                        £{
                          (() => {
                            const ms = result.totalValue / 12 || 0;
                            const low = Math.floor(ms * 0.95);
                            const high = Math.ceil(ms * 1.05);
                            return `${low}–${high}`;
                          })()
                        }
                      </span>
                    </div>

                    <div>
                      <strong className="payback-period-label">Payback Period</strong>
                      <span className="payback-period-value">
                        {(() => {
                          const pb = result.simplePayback || 0;
                          const low = Math.floor(pb);
                          const high = Math.ceil(pb);
                          return `${low}–${high} years`;
                        })()}
                      </span>
                    </div>

                    <div>
                      <strong className="co2-avoided-label">CO₂ Avoided / Year</strong>
                      <span className="co2-avoided-value">
                        {result.co2Avoided?.toFixed(2) || 0} t
                      </span>
                    </div>
                  </div>

                  <p className="estimate-note">
                    This estimate includes standard scaffolding costs
                  </p>
                </div>

                <div className="info-grid">
                  <div className="info-left">
                    <div className="finance-card">
                      <h4>Finance Available</h4>
                      <p className="finance-highlight">
                        £{result.monthlyPayment?.toFixed(0) || 0} <span className="finance-span">/ month</span> 
                      </p>
                      <p>Pay monthly with finance.</p>
                    </div>

                    <div className="survey-card">
                      <h4>Free Roof Survey with No‑Obligation Repair Quote</h4>
                      <p>
                        Before installation, we carry out a full roof survey. If any issues are found, we'll provide a no‑obligation quote for repairs — ensuring your roof is safe, secure and ready for solar.
                      </p>
                    </div>
                  </div>
                  <div className="standard-card">
                    <h4>What's included as Standard</h4>
                    <ul>
                      <li>Premium all‑black solar panels</li>
                      <li>Matching all‑black mounting rails</li>
                      <li>Inverter to convert energy for home use</li>
                      <li>Bird proofing to protect your system</li>
                      <li>Generation meter to track performance</li>
                      <li>DNO application (G98/G99) handled on your behalf</li>
                      <li>MCS certification for compliance and incentives</li>
                      <li>Full installation by accredited engineers</li>
                    </ul>
                  </div>
                </div>

                <div className="email-section">
                  <input
                    type="email"
                    className="email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your Email Address"
                  />
                  <button className="email-btn" 
                  onClick={() => sendEmail({
                    estimate: result.totalInstalledCost?.toFixed(0) || 0,
        panels: panels,
        battery: battery,
        evCharger: evCharger,  
      })
    }
                   disabled={loading}>
        {loading ? "Sending..." : "Email My Estimate →"}
      </button>
      {message && <p className="email-message">{message}</p>}
    </div>
                <button className="quote-btn" onClick={() => { setShowStep3(true);
                   window.scrollTo({ top: 0, behavior: "smooth" });}}
 >
                      Get My Tailored Quote →
                  </button>
              </div>
            </div>
          ) : (
            <div className="commercial-layout">
              <div className="top-row">
                <div className="commercial-estimate-card estimate-card" style={{ backgroundImage: `url('https://electroenergy.co.uk/wp-content/uploads/2025/11/estimate-bg.png')` }}>
                  <div className="estimate-header">Your Estimate</div>
                  <div className="estimate-value">£{result.totalInstalledCost?.toFixed(0) || 0}</div>
                  <div className="savings-bar">
                    <div className="savings-fill" style={{ width: "80%" }}></div>
                  </div>
                  <div className="savings-range">Typical bill savings of 120–139%</div>

                  <div className="stats-row wide">
                    <div>
                      <strong>Monthly Savings</strong>
                      <br />
                      £{
                        (() => {
                          const ms = result.totalValue / 12 || 0;
                          const low = Math.floor(ms * 0.95);
                          const high = Math.ceil(ms * 1.05);
                          return `${low}–${high}`;
                        })()
                      }
                    </div>
                    <div>
                      <strong>Payback Period</strong>
                      <br />
                      {(() => {
                        const pb = result.simplePayback || 0;
                        const low = Math.floor(pb);
                        const high = Math.ceil(pb);
                        return `${low}–${high} years`;
                      })()}
                    </div>
                    <div>
                      <strong>CO₂ Avoided / Year</strong>
                      <br />
                      {result.co2Avoided?.toFixed(2) || 0} t
                    </div>
                  </div>

                  <p className="estimate-note">
                    This estimate covers basic access equipment only. It does not include structural calculations, any extra LV panel requirements, or cabling/containment beyond 15 metres. The estimate is based on installation on a flat metal roof.
                  </p>
                </div>
                <div className="commercial-finance-card finance-card">
                  <h4>Finance Available</h4>
                  <p className="finance-highlight">
                    £{result.monthlyPayment?.toFixed(0) || 0} / month
                  </p>
                  <p>Pay monthly with finance.</p>
                </div>
              </div>
              <div className={`mobile-sticky-estimate ${hideSticky2 ? "hidden" : ""}`}>
  <div className="estimate-label">Your Estimate</div>
  <div className="estimate-value">£{result.totalInstalledCost?.toFixed(0) || 0}</div>
</div>
              <div className="bottom-row">
                <div className="panel-card commercial-panel">
                  <h3>Select Number of Panels?
                    <span className="info-icon">
                      i
                      <span className="tooltip-text">
                        Aiko Neostar 3S Gen 3 — high-efficiency all-black N-Type solar panel with advanced ABC cell technology, sleek design, and 25-year warranty.
                      </span>
                    </span>
                  </h3>
                  <p className="card-helper">Use the buttons to choose:</p>
                  <div className="panel-selector">
                    <button
                      className="panel-btn"
                      onClick={() => setPanels((p) => Math.max(minPanels, p - 1))}
                    >
                      −
                    </button>
                    <span className="panel-count">{panels}</span>
                    <button
                      className="panel-btn"
                      onClick={() => setPanels((p) => Math.min(maxPanels, p + 1))}
                    >
                      +
                    </button>
                  </div>
                  <p className="range-text">
                    <span className="star-icon">★</span> (Range: {minPanels}–{maxPanels} panels)
                  </p>
                </div>
                <div className="commercial-email-card">
                  <input
                    type="email"
                    className="commercial-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your Email Address"
                  />
                  <button className="commercial-email-btn" 
                  onClick={() => sendEmail({
                    estimate: result.totalInstalledCost?.toFixed(0) || 0,
                   panels: panels,
      })
    }>
                      {loading ? "Sending..." : "Email My Estimate →"}
                  </button>
                   {message && <p className="email-message">{message}</p>}
                  <button className="quote-btn" onClick={() => { setShowStep3(true);
                   window.scrollTo({ top: 0, behavior: "smooth" });}}
 >
                      Get My Tailored Quote →
                  </button>
                  
                </div>
              </div>
            </div> 
          )}
        </div>
      )}  
      
          </div>
          
          
  );
}